export type HolidayCalendar = 'US' | 'UK' | 'CA' | 'AU' | 'DE' | 'FR' | 'NONE';

export const CALENDAR_LABELS: Record<HolidayCalendar, string> = {
  US:   '🇺🇸 United States',
  UK:   '🇬🇧 United Kingdom',
  CA:   '🇨🇦 Canada',
  AU:   '🇦🇺 Australia',
  DE:   '🇩🇪 Germany',
  FR:   '🇫🇷 France',
  NONE: 'None (all weekdays)',
};

export function fmtDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Saturday → Friday, Sunday → Monday
function observed(d: Date): Date {
  const dow = d.getDay();
  if (dow === 6) return new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1);
  if (dow === 0) return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
  return d;
}

function shiftDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

// nth occurrence of weekday (0=Sun…6=Sat) in a month (0-indexed)
function nthWeekday(year: number, month: number, weekday: number, n: number): Date {
  let count = 0;
  for (let day = 1; day <= 31; day++) {
    const d = new Date(year, month, day);
    if (d.getMonth() !== month) break;
    if (d.getDay() === weekday && ++count === n) return d;
  }
  return new Date(year, month, 1);
}

// Last occurrence of weekday in a month
function lastWeekday(year: number, month: number, weekday: number): Date {
  const d = new Date(year, month + 1, 0); // last calendar day of month
  while (d.getDay() !== weekday) d.setDate(d.getDate() - 1);
  return new Date(d);
}

// Mon on or before a given date
function monOnOrBefore(year: number, month: number, day: number): Date {
  const d = new Date(year, month, day);
  while (d.getDay() !== 1) d.setDate(d.getDate() - 1);
  return new Date(d);
}

// Easter Sunday — Anonymous Gregorian algorithm
function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
  const day   = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month, day);
}

// Christmas + Boxing Day with full observed-day substitution for UK/CA/AU
function christmasAndBoxing(year: number): string[] {
  const dec25 = new Date(year, 11, 25).getDay();
  // Fri: Dec 25 ok, Dec 26 (Sat) → Mon Dec 28
  if (dec25 === 5) return [fmtDate(new Date(year, 11, 25)), fmtDate(new Date(year, 11, 28))];
  // Sat: Dec 25 → Mon Dec 27, Dec 26 (Sun) → Tue Dec 28
  if (dec25 === 6) return [fmtDate(new Date(year, 11, 27)), fmtDate(new Date(year, 11, 28))];
  // Sun: Dec 25 → Mon Dec 26, Dec 26 → Tue Dec 27
  if (dec25 === 0) return [fmtDate(new Date(year, 11, 26)), fmtDate(new Date(year, 11, 27))];
  return [fmtDate(new Date(year, 11, 25)), fmtDate(new Date(year, 11, 26))];
}

// ─── Country implementations ──────────────────────────────────────────────────

function getUSHolidays(year: number): Set<string> {
  const obs = (m: number, day: number) => fmtDate(observed(new Date(year, m, day)));
  return new Set([
    obs(0, 1),                                         // New Year's Day
    fmtDate(nthWeekday(year, 0, 1, 3)),               // MLK Day (3rd Mon Jan)
    fmtDate(nthWeekday(year, 1, 1, 3)),               // Presidents Day (3rd Mon Feb)
    fmtDate(lastWeekday(year, 4, 1)),                 // Memorial Day (last Mon May)
    obs(5, 19),                                        // Juneteenth
    obs(6, 4),                                         // Independence Day
    fmtDate(nthWeekday(year, 8, 1, 1)),               // Labor Day (1st Mon Sep)
    fmtDate(nthWeekday(year, 9, 1, 2)),               // Columbus Day (2nd Mon Oct)
    obs(10, 11),                                       // Veterans Day
    fmtDate(nthWeekday(year, 10, 4, 4)),              // Thanksgiving (4th Thu Nov)
    obs(11, 25),                                       // Christmas
  ]);
}

function getUKHolidays(year: number): Set<string> {
  const e = easterSunday(year);
  return new Set([
    fmtDate(observed(new Date(year, 0, 1))),           // New Year's Day
    fmtDate(shiftDays(e, -2)),                         // Good Friday
    fmtDate(shiftDays(e, 1)),                          // Easter Monday
    fmtDate(nthWeekday(year, 4, 1, 1)),               // Early May Bank Holiday (1st Mon May)
    fmtDate(lastWeekday(year, 4, 1)),                 // Spring Bank Holiday (last Mon May)
    fmtDate(lastWeekday(year, 7, 1)),                 // Summer Bank Holiday (last Mon Aug)
    ...christmasAndBoxing(year),
  ]);
}

function getCAHolidays(year: number): Set<string> {
  const e = easterSunday(year);
  const obs = (m: number, day: number) => fmtDate(observed(new Date(year, m, day)));
  return new Set([
    obs(0, 1),                                         // New Year's Day
    fmtDate(shiftDays(e, -2)),                         // Good Friday
    fmtDate(monOnOrBefore(year, 4, 25)),              // Victoria Day (Mon on/before May 25)
    obs(6, 1),                                         // Canada Day
    fmtDate(nthWeekday(year, 8, 1, 1)),               // Labour Day (1st Mon Sep)
    obs(9, 30),                                        // National Day for Truth & Reconciliation
    fmtDate(nthWeekday(year, 9, 1, 2)),               // Thanksgiving (2nd Mon Oct)
    obs(10, 11),                                       // Remembrance Day
    ...christmasAndBoxing(year),
  ]);
}

function getAUHolidays(year: number): Set<string> {
  const e = easterSunday(year);
  const obs = (m: number, day: number) => fmtDate(observed(new Date(year, m, day)));
  return new Set([
    obs(0, 1),                                         // New Year's Day
    obs(0, 26),                                        // Australia Day
    fmtDate(shiftDays(e, -2)),                         // Good Friday
    fmtDate(shiftDays(e, 1)),                          // Easter Monday
    obs(3, 25),                                        // ANZAC Day
    fmtDate(nthWeekday(year, 5, 1, 2)),               // King's Birthday (2nd Mon Jun — most states)
    ...christmasAndBoxing(year),
  ]);
}

function getDEHolidays(year: number): Set<string> {
  const e = easterSunday(year);
  return new Set([
    fmtDate(new Date(year, 0, 1)),                     // New Year's Day
    fmtDate(shiftDays(e, -2)),                         // Good Friday
    fmtDate(shiftDays(e, 1)),                          // Easter Monday
    fmtDate(new Date(year, 4, 1)),                     // Labour Day
    fmtDate(shiftDays(e, 39)),                         // Ascension Day
    fmtDate(shiftDays(e, 50)),                         // Whit Monday
    fmtDate(new Date(year, 9, 3)),                     // German Unity Day
    fmtDate(new Date(year, 11, 25)),                   // Christmas Day
    fmtDate(new Date(year, 11, 26)),                   // Second Day of Christmas
  ]);
}

function getFRHolidays(year: number): Set<string> {
  const e = easterSunday(year);
  return new Set([
    fmtDate(new Date(year, 0, 1)),                     // New Year's Day
    fmtDate(shiftDays(e, 1)),                          // Easter Monday
    fmtDate(new Date(year, 4, 1)),                     // Labour Day
    fmtDate(new Date(year, 4, 8)),                     // Victory in Europe Day
    fmtDate(shiftDays(e, 39)),                         // Ascension Day
    fmtDate(shiftDays(e, 50)),                         // Whit Monday
    fmtDate(new Date(year, 6, 14)),                    // Bastille Day
    fmtDate(new Date(year, 7, 15)),                    // Assumption of Mary
    fmtDate(new Date(year, 10, 1)),                    // All Saints Day
    fmtDate(new Date(year, 10, 11)),                   // Armistice Day
    fmtDate(new Date(year, 11, 25)),                   // Christmas Day
  ]);
}

// ─── Public API ───────────────────────────────────────────────────────────────

export function getHolidays(year: number, calendar: HolidayCalendar): Set<string> {
  switch (calendar) {
    case 'US':   return getUSHolidays(year);
    case 'UK':   return getUKHolidays(year);
    case 'CA':   return getCAHolidays(year);
    case 'AU':   return getAUHolidays(year);
    case 'DE':   return getDEHolidays(year);
    case 'FR':   return getFRHolidays(year);
    case 'NONE': return new Set();
  }
}
